






































































































































































































































































































































import Vue from 'vue';

import {
  Component,
  Prop,
  Watch,
} from 'vue-property-decorator';

import {
  RateTableModel,
  WorkTypeCell,
  CellVerdict,
  SheetGap,
  defaultTable,
  normalizeSupplierId,
  normalizeSupplierIds,
} from '@/components/RatesPage/types';
import {
  Band,
  BandEdit,
  BandRelation,
  BandVerdict,
  bandKey,
  bandLabel,
  bandTitle,
  coverageGaps,
  formatBound,
  moveLowerBound,
  neighbourBelow,
  probeBand,
  rangesTitle,
  removeBand as removeBandFromSet,
  isChained,
  sortBandsForDisplay,
  splitAt,
  verdictsByBandKey,
} from '@/components/RatesPage/bands';
import { formatSupplierLabel, sortSuppliers } from '@/utils/supplierDisplay';

interface ChangableObject {
  inChange: boolean;
  value: any;
}

type PropertyCode = 'performance' | 'quality';

interface LocalCell {
  rate: string;
  tariffId: number | null;
  verdict: CellVerdict | null;
  rateIds: { performance: number | null; quality: number | null };
}

interface ViewChip {
  text: string;
  color: string;
  hint: string;
}

interface Finding {
  text: string;
  icon: string;
  color: string;
}

interface ViewCell {
  key: string;
  band: Band;
  rate: string;
  editable: boolean;
  cssClass: string;
  hint: string;
}

interface ViewRow {
  key: string;
  band: Band;
  label: string;
  chip: ViewChip | null;
  cells: Array<ViewCell>;
  removable: boolean;
  removeHint: string;
  removeBlockedHint: string;
}

const PROPERTY_NAMES: Record<PropertyCode, string> = {
  performance: 'производительность',
  quality: 'качество',
};

@Component({
  components: {
  },
})
export default class RatesTable extends Vue {
  @Prop({
    type: Object,
    default: false,
  }) readonly workType!: WorkTypeCell

  @Prop()
  readonly suppliers!: any;

  @Prop()
  readonly selectedSuppliers!: any;

  @Prop()
  readonly availableSuppliers!: any;

  @Prop()
  readonly sheetKey: string | undefined;

  comment = this.workType?.comment || ''

  realSelectedSuppliers: number[] = normalizeSupplierIds(this.selectedSuppliers);

  bandChanges: Record<PropertyCode, Record<string, ChangableObject>> = {
    performance: {},
    quality: {},
  };

  probePerformance = '';

  probeQuality = '';

  created() {
    this.syncSelectedSuppliers();
  }

  @Watch('selectedSuppliers', { deep: true })
  syncSelectedSuppliers() {
    this.realSelectedSuppliers = normalizeSupplierIds(this.selectedSuppliers);
  }

  updateComment() {
    this.$emit('updateComment', {
      sheetKey: this.sheetKey,
      comment: this.comment,
    });
  }

  deleteTariff() {
    this.$emit('deleteTariff', this.sheetKey);
  }

  get cells(): Array<RateTableModel> {
    return this.workType?.cells || [];
  }

  /**
   * Полосы свойства — по хранимому интервалу, а не по нижней границе. Две полосы с одной
   * нижней границей раньше склеивались в одну строку, и цена второй пропадала с экрана
   * (задача 10577): расчёт её видел, пользователь — нет.
   */
  bandsOf(property: PropertyCode): Array<Band> {
    const seen: Record<string, boolean> = {};
    const bands: Array<Band> = [];
    this.cells.forEach((cell) => {
      const raw = cell[property];
      if (!raw || (raw.from === null && raw.from !== 0)) {
        return;
      }
      const band: Band = { from: Number(raw.from), to: raw.to === null ? null : Number(raw.to) };
      const key = bandKey(band);
      if (seen[key]) {
        return;
      }
      seen[key] = true;
      bands.push(band);
    });
    return sortBandsForDisplay(bands);
  }

  get performanceBands(): Array<Band> {
    return this.bandsOf('performance');
  }

  get qualityBands(): Array<Band> {
    return this.bandsOf('quality');
  }

  get cellIndex(): Record<string, LocalCell> {
    const index: Record<string, LocalCell> = {};
    this.cells.forEach((cell) => {
      const perf = cell.performance;
      const qual = cell.quality;
      if (!perf || !qual) {
        return;
      }
      const key = this.cellKey(
        { from: Number(perf.from), to: perf.to === null ? null : Number(perf.to) },
        { from: Number(qual.from), to: qual.to === null ? null : Number(qual.to) },
      );
      index[key] = {
        rate: String(cell.rate),
        tariffId: cell.tariff_id ?? null,
        verdict: cell.verdict ?? null,
        rateIds: { performance: perf.rate_id ?? null, quality: qual.rate_id ?? null },
      };
    });
    return index;
  }

  cellKey(perf: Band, qual: Band): string {
    return `${bandKey(perf)}×${bandKey(qual)}`;
  }

  verdictsOf(property: PropertyCode): Record<string, BandVerdict> {
    const sheetBands = this.workType?.bands;
    return verdictsByBandKey(sheetBands ? sheetBands[property] : null);
  }

  get performanceVerdicts(): Record<string, BandVerdict> {
    return this.verdictsOf('performance');
  }

  get qualityVerdicts(): Record<string, BandVerdict> {
    return this.verdictsOf('quality');
  }

  get gaps(): Array<SheetGap> {
    return this.workType?.gaps || [];
  }

  /**
   * Вердикт считается на бэкенде по хранимым полосам. Как только пользователь сдвинул
   * границу, часть полос вердикта не имеет — красить по нему уже нельзя, надо сохранить.
   */
  get verdictStale(): boolean {
    const hasAnyVerdict = Object.keys(this.performanceVerdicts).length > 0
      || Object.keys(this.qualityVerdicts).length > 0;
    if (!hasAnyVerdict) {
      return false;
    }
    const missing = (bands: Array<Band>, verdicts: Record<string, BandVerdict>) => bands
      .some((band) => !verdicts[bandKey(band)]);

    return missing(this.performanceBands, this.performanceVerdicts)
      || missing(this.qualityBands, this.qualityVerdicts);
  }

  get tableHeaders() {
    return [
      { text: 'производительность \\ качество', value: 'band', sortable: false },
      ...this.qualityBands.map((band) => ({
        text: bandLabel(band), value: bandKey(band), sortable: false,
      })),
    ];
  }

  bandChip(property: PropertyCode, band: Band): ViewChip | null {
    const verdict = this.verdictsOf(property)[bandKey(band)];
    if (!verdict) {
      return null;
    }
    if (verdict.applies === 'none') {
      return {
        text: 'не применяется',
        color: 'error',
        hint: `Расчёт никогда не приходит в эту полосу: её перекрывает ${this.relationLabels(verdict.shadowed_by)}. `
          + `Цены в этой строке в закрытие не попадут.${this.culpritNote(property, verdict.shadowed_by)}`,
      };
    }
    if (verdict.applies === 'partial') {
      return {
        text: `частично: ${rangesTitle(verdict.ranges)}`,
        color: 'warning',
        hint: `Полоса выигрывает только при ${rangesTitle(verdict.ranges)}; на остальном её перекрывает `
          + `${this.relationLabels(verdict.shadowed_by)}.${this.culpritNote(property, verdict.shadowed_by)}`,
      };
    }
    if (verdict.shadows.length) {
      return {
        text: 'перекрывает свои полосы',
        color: 'orange darken-2',
        // «Узкая» и «широкая» тут ни при чём: выигрывает заведённая раньше, какой бы
        // ширины она ни была (`ORDER BY pr.id ASC` в PropertyRateLookupSql).
        hint: `Эта полоса забирает проценты у других полос: ${this.relationLabels(verdict.shadows)}.`
          + ' Цены на перекрытых полосах в закрытие не попадут.',
      };
    }
    return null;
  }

  /**
   * Какая цена уйдёт в закрытие вместо мёртвой строки. Полоса принадлежит листу, поэтому
   * перекрывающая полоса всегда заведена здесь же — искать её в других листах не нужно.
   * Без этой приписки мёртвая строка говорит «её перекрывает полоса от 0 до 100 %» и не
   * сообщает, какая именно цена придёт вместо неё.
   */
  culpritNote(property: PropertyCode, relations: Array<BandRelation>): string {
    const parts: Array<string> = [];

    relations.forEach((relation) => {
      if (relation.from === null) {
        return;
      }
      const relationBand: Band = { from: relation.from as number, to: relation.to };
      const prices = this.pricesOnBand(property, relationBand);

      if (prices.length) {
        parts.push(`В закрытие идёт цена перекрывающей полосы ${bandTitle(relationBand)}: `
          + `${prices.join(', ')}.`);
        return;
      }
      parts.push(`Цены на перекрывающей полосе ${bandTitle(relationBand)} нет — `
        + 'в закрытие уйдёт 0.');
    });

    return parts.length ? ` ${parts.join(' ')}` : '';
  }

  relationLabels(relations: Array<{ from: number | null; to: number | null }>): string {
    if (!relations.length) {
      return 'другая полоса';
    }
    return relations
      .map((relation) => (relation.from === null
        ? 'другая полоса'
        : bandTitle({ from: relation.from, to: relation.to })))
      .join(', ');
  }

  get rows(): Array<ViewRow> {
    const index = this.cellIndex;
    const verdicts = this.performanceVerdicts;

    return this.performanceBands.map((band) => {
      const key = bandKey(band);
      const verdict = verdicts[key];
      const removable = this.isRemovable('performance', band);

      return {
        key,
        band,
        label: bandLabel(band),
        chip: this.bandChip('performance', band),
        removable,
        removeHint: `Убрать полосу ${bandTitle(band)} из этого тарифа.`,
        removeBlockedHint: this.removeBlockedHint('performance', band),
        cells: this.qualityBands.map((qualBand) => {
          const cellKey = this.cellKey(band, qualBand);
          const cell = index[cellKey];
          const cellVerdict = cell?.verdict || null;
          const dead = verdict?.applies === 'none'
            || this.qualityVerdicts[bandKey(qualBand)]?.applies === 'none'
            || cellVerdict?.applies === 'none';

          return {
            key: cellKey,
            band: qualBand,
            rate: cell ? cell.rate : '0',
            editable: !dead,
            cssClass: dead ? 'cell--dead' : '',
            hint: 'Эта цена не применяется ни при одном проценте — расчёт берёт цену перекрывающей полосы. '
              + 'Уберите перекрывающую полосу, чтобы цена ожила.',
          };
        }),
      };
    });
  }

  get columns() {
    return this.qualityBands.map((band) => ({
      key: bandKey(band),
      band,
      label: bandLabel(band),
      chip: this.bandChip('quality', band),
      removable: this.isRemovable('quality', band),
    }));
  }

  rowClass(row: ViewRow): string {
    const verdict = this.performanceVerdicts[row.key];
    if (!verdict) {
      return '';
    }
    if (verdict.applies === 'none') {
      return 'band-row--dead';
    }
    if (verdict.applies === 'partial') {
      return 'band-row--partial';
    }
    if (verdict.shadows.length) {
      return 'band-row--conflict';
    }
    return '';
  }

  /** Замечания под таблицей: кто кого перекрывает и что из этого следует по деньгам. */
  get findings(): Array<Finding> {
    const result: Array<Finding> = [];

    (['performance', 'quality'] as Array<PropertyCode>).forEach((property) => {
      const verdicts = this.verdictsOf(property);
      const own = this.bandsOf(property).map((band) => bandKey(band));

      own.forEach((key) => {
        const verdict = verdicts[key];
        if (!verdict) {
          return;
        }
        const band: Band = { from: verdict.from, to: verdict.to };
        const prices = this.pricesOnBand(property, band);
        const priceText = prices.length
          ? ` ${prices.length === 1 ? 'Цена' : 'Цены'} ${prices.join(', ')} в закрытие не ${prices.length === 1 ? 'попадёт' : 'попадут'}.`
          : '';

        if (verdict.applies === 'none') {
          result.push({
            icon: 'mdi-alert-circle',
            color: 'error',
            text: `${PROPERTY_NAMES[property]}: полоса ${bandTitle(band)} не выигрывает ни при одном проценте — `
              + `её перекрывает ${this.relationLabels(verdict.shadowed_by)}.${priceText}`
              + `${this.culpritNote(property, verdict.shadowed_by)}`,
          });
          return;
        }
        if (verdict.applies === 'partial') {
          result.push({
            icon: 'mdi-alert',
            color: 'warning',
            text: `${PROPERTY_NAMES[property]}: полоса ${bandTitle(band)} применяется только при `
              + `${rangesTitle(verdict.ranges)} — на остальном её перекрывает `
              + `${this.relationLabels(verdict.shadowed_by)}.`
              + `${this.culpritNote(property, verdict.shadowed_by)}`,
          });
          return;
        }
        if (verdict.shadows.length) {
          result.push({
            icon: 'mdi-alert-outline',
            color: 'orange darken-2',
            text: `${PROPERTY_NAMES[property]}: полоса ${bandTitle(band)} забирает проценты у других полос `
              + `(${this.relationLabels(verdict.shadows)}).`,
          });
        }
      });
    });

    this.gaps.forEach((gap) => {
      const name = PROPERTY_NAMES[gap.property] || gap.property;
      const range = gap.to === null
        ? `${formatBound(gap.from)} % и выше`
        : `${formatBound(gap.from)}–${formatBound(gap.to)} %`;
      result.push({
        icon: 'mdi-help-circle',
        color: 'info',
        text: `${name}: проценты ${range} не покрыты ни одной полосой — в закрытие уйдёт 0 без ошибки.`,
      });
    });

    return result;
  }

  /** Разные цены, заведённые на этой полосе в этом листе. */
  pricesOnBand(property: PropertyCode, band: Band): Array<string> {
    const key = bandKey(band);
    const prices: Array<string> = [];
    this.cells.forEach((cell) => {
      const raw = cell[property];
      if (!raw) {
        return;
      }
      const cellBand: Band = { from: Number(raw.from), to: raw.to === null ? null : Number(raw.to) };
      if (bandKey(cellBand) !== key) {
        return;
      }
      const price = String(cell.rate);
      if (!prices.includes(price)) {
        prices.push(price);
      }
    });
    return prices;
  }

  get probeResult(): { text: string; cssClass: string } | null {
    const perfPercent = parseFloat(this.probePerformance);
    const qualPercent = parseFloat(this.probeQuality);
    if (!Number.isFinite(perfPercent) || !Number.isFinite(qualPercent)) {
      return null;
    }
    if (this.verdictStale) {
      return {
        text: 'Проверка доступна после сохранения: границы полос изменены.',
        cssClass: 'probe-result probe-result--unknown',
      };
    }

    const perf = probeBand(this.performanceBands, this.performanceVerdicts, perfPercent);
    const qual = probeBand(this.qualityBands, this.qualityVerdicts, qualPercent);

    if (!perf.band || !qual.band) {
      const missing = !perf.band ? 'производительности' : 'качества';
      return {
        text: `Ни одна полоса ${missing} не покрывает этот процент — в закрытие уйдёт 0.`,
        cssClass: 'probe-result probe-result--gap',
      };
    }

    const cell = this.cellIndex[this.cellKey(perf.band, qual.band)];
    if (!cell) {
      return {
        text: `Полосы ${bandTitle(perf.band)} × ${bandTitle(qual.band)} выигрывают, но цены на этой паре нет — `
          + 'в закрытие уйдёт 0.',
        cssClass: 'probe-result probe-result--gap',
      };
    }

    let text = `В закрытие пойдёт ${cell.rate}: полоса ${bandTitle(perf.band)} × ${bandTitle(qual.band)}.`;
    const alternatives: Array<string> = [];
    perf.shadowed.forEach((band) => {
      const other = this.cellIndex[this.cellKey(band, qual.band as Band)];
      if (other && other.rate !== cell.rate) {
        alternatives.push(`${other.rate} (полоса ${bandTitle(band)} перекрыта)`);
      }
    });
    qual.shadowed.forEach((band) => {
      const other = this.cellIndex[this.cellKey(perf.band as Band, band)];
      if (other && other.rate !== cell.rate) {
        alternatives.push(`${other.rate} (полоса ${bandTitle(band)} перекрыта)`);
      }
    });
    if (alternatives.length) {
      text += ` ⚠ А не ${alternatives.join(', ')}.`;
    }

    return {
      text,
      cssClass: alternatives.length ? 'probe-result probe-result--warning' : 'probe-result',
    };
  }

  selectSupplier() {
    this.$emit('refreshSuppliers', {
      suppliers: this.realSelectedSuppliers,
      key: this.sheetKey,
    });
  }

  get availSupTets() {
    const selectedIds = new Set(this.realSelectedSuppliers);
    const availableIds = new Set(
      (this.availableSuppliers || []).map((id: unknown) => normalizeSupplierId(id)),
    );
    const items: any[] = [];
    const seen = new Set<number>();

    this.suppliers.forEach((sup: any) => {
      const id = normalizeSupplierId(sup.id);
      if (id < 1 || seen.has(id)) {
        return;
      }
      // показываем только уже выбранных в этом листе ИЛИ доступных для выбора;
      // новых снятых с публикации в availableSuppliers нет (RatesSheet их исключает).
      if (selectedIds.has(id) || availableIds.has(id)) {
        seen.add(id);
        items.push({ ...sup, id, display_name: formatSupplierLabel(sup) });
      }
    });

    selectedIds.forEach((id) => {
      if (seen.has(id)) {
        return;
      }
      seen.add(id);
      items.push({
        id,
        legal_name: `Поставщик #${id}`,
        display_name: `Поставщик #${id}`,
      });
    });

    return sortSuppliers(items);
  }

  restoreDefault() {
    this.$emit('changeRateTable', {
      cells: JSON.parse(JSON.stringify(defaultTable)),
      suppliers: this.realSelectedSuppliers,
      key: this.workType.key,
    });
  }

  /**
   * Полосы и цены уезжают в сохранение явными интервалами: `PrepareCells` ключует полосу
   * по `from`/`to` из payload, поэтому строка, которую пользователь не трогал, остаётся
   * той же полосой. Прежний код выводил `to` по соседней строке — из-за этого
   * пересохранение вида работ с перекрытием теряло полосы и переписывало цены.
   */
  emitTable(
    performance: Array<Band>,
    quality: Array<Band>,
    index: Record<string, LocalCell>,
  ) {
    const cells: Array<RateTableModel> = [];

    sortBandsForDisplay(performance).forEach((perf) => {
      sortBandsForDisplay(quality).forEach((qual) => {
        const cell = index[this.cellKey(perf, qual)];
        const rate = parseFloat(cell ? cell.rate : '0');
        const built: RateTableModel = {
          performance: { from: perf.from, to: perf.to },
          quality: { from: qual.from, to: qual.to },
          rate: Number.isFinite(rate) ? rate : 0,
        };
        // rate_id / tariff_id / verdict несём обратно только у нетронутых интервалов:
        // после сдвига границы вердикт бэкенда к новой полосе не относится.
        if (cell) {
          if (cell.rateIds.performance) {
            built.performance.rate_id = cell.rateIds.performance;
          }
          if (cell.rateIds.quality) {
            built.quality.rate_id = cell.rateIds.quality;
          }
          if (cell.tariffId) {
            built.tariff_id = cell.tariffId;
          }
          if (cell.verdict) {
            built.verdict = cell.verdict;
          }
        }
        cells.push(built);
      });
    });

    this.$emit('changeRateTable', {
      cells,
      suppliers: this.realSelectedSuppliers,
      key: this.workType.key,
    });
  }

  /**
   * Перенос цен при правке полос: ключ строки — интервал, значит сдвиг границы меняет
   * ключ. `moved` может указывать на две новые полосы (полоса-затычка `0 – низ` повторяет
   * цены прежней нижней полосы) — поэтому цены копируются, а не переставляются.
   */
  applyEdit(property: PropertyCode, edit: BandEdit) {
    const oldBands = this.bandsOf(property);
    const newKeys = edit.bands.map((band) => bandKey(band));
    const targets: Record<string, Array<string>> = {};

    edit.moved.forEach((move) => {
      const source = bandKey(move.from);
      if (!targets[source]) {
        targets[source] = [];
      }
      targets[source].push(bandKey(move.to));
    });
    oldBands.forEach((band) => {
      const key = bandKey(band);
      if (!targets[key] && newKeys.includes(key)) {
        targets[key] = [key];
      }
    });

    const index: Record<string, LocalCell> = {};
    const geometryChanged = (key: string, mapped: string) => key !== mapped;

    Object.entries(this.cellIndex).forEach(([key, cell]) => {
      const [perfKey, qualKey] = key.split('×');
      const editedKey = property === 'performance' ? perfKey : qualKey;
      (targets[editedKey] || []).forEach((mapped) => {
        const nextKey = property === 'performance'
          ? `${mapped}×${qualKey}`
          : `${perfKey}×${mapped}`;
        index[nextKey] = geometryChanged(editedKey, mapped)
          ? {
            rate: cell.rate,
            tariffId: null,
            verdict: null,
            rateIds: property === 'performance'
              ? { performance: null, quality: cell.rateIds.quality }
              : { performance: cell.rateIds.performance, quality: null },
          }
          : cell;
      });
    });

    if (property === 'performance') {
      this.emitTable(edit.bands, this.qualityBands, index);
    } else {
      this.emitTable(this.performanceBands, edit.bands, index);
    }
  }

  changeCell(perf: Band, qual: Band, value: string | number) {
    const index = { ...this.cellIndex };
    const key = this.cellKey(perf, qual);
    const current = index[key];
    index[key] = {
      rate: String(value),
      tariffId: current?.tariffId ?? null,
      verdict: current?.verdict ?? null,
      rateIds: current?.rateIds ?? { performance: null, quality: null },
    };
    this.emitTable(this.performanceBands, this.qualityBands, index);
  }

  /** «Минус» убирает границу между этой полосой и той, что ниже: они сливаются в одну. */
  canRemoveBoundary(property: PropertyCode, band: Band): boolean {
    const bands = this.bandsOf(property);
    return band.from !== 0 && isChained(bands, band) && neighbourBelow(bands, band) !== null;
  }

  removeBoundary(property: PropertyCode, band: Band) {
    const bands = this.bandsOf(property);
    const below = neighbourBelow(bands, band);
    if (!below) {
      return;
    }
    this.applyEdit(property, removeBandFromSet(bands, band));
  }

  canSplit(property: PropertyCode, band: Band): boolean {
    const bands = this.bandsOf(property);
    return band.from !== 0 && neighbourBelow(bands, band) !== null;
  }

  /** «Плюс» делит пополам полосу, лежащую ниже — как это работало до задачи 10577. */
  splitBelow(property: PropertyCode, band: Band) {
    const bands = this.bandsOf(property);
    const below = neighbourBelow(bands, band);
    if (!below) {
      return;
    }
    const width = band.from - below.from;
    const boundary = Math.round((width === 0 ? band.from - 0.5 : band.from - (width / 2)) * 100) / 100;
    if (boundary <= below.from) {
      return;
    }
    this.applyEdit(property, splitAt(bands, boundary));
  }

  /**
   * Убрать полосу целиком вместе с её ценами. Разрешено только когда покрытие `[0, ∞)`
   * остаётся целым: дырка не даёт ошибки, расчёт молча пишет 0 в закрытие.
   */
  isRemovable(property: PropertyCode, band: Band): boolean {
    const bands = this.bandsOf(property);
    if (bands.length < 2) {
      return false;
    }
    const verdict = this.verdictsOf(property)[bandKey(band)];
    // Убирать предлагаем только то, что мешает: мёртвую полосу или ту, что перекрывает чужую.
    const worthRemoving = !!verdict && (verdict.applies !== 'full' || verdict.shadows.length > 0);
    if (!worthRemoving) {
      return false;
    }
    const edit = removeBandFromSet(bands, band);
    return coverageGaps(edit.bands).length <= coverageGaps(bands).length;
  }

  removeBlockedHint(property: PropertyCode, band: Band): string {
    const verdict = this.verdictsOf(property)[bandKey(band)];
    if (!verdict) {
      return '';
    }
    if (verdict.applies === 'full' && !verdict.shadows.length) {
      return '';
    }
    return `Убрать полосу ${bandTitle(band)} нельзя: без неё часть процентов останется без полосы, `
      + 'и в закрытие уйдёт 0.';
  }

  removeBand(property: PropertyCode, band: Band) {
    if (!this.isRemovable(property, band)) {
      return;
    }
    this.applyEdit(property, removeBandFromSet(this.bandsOf(property), band));
  }

  startBoundChange(property: PropertyCode, item: { key: string; band: Band }) {
    if (!isChained(this.bandsOf(property), item.band)) {
      this.$bus.$emit(
        'notification',
        'Границу перекрывающейся полосы менять нельзя: сначала уберите лишнюю полосу.',
        'warning',
      );
      return;
    }
    Vue.set(this.bandChanges[property], item.key, {
      inChange: true,
      value: item.band.from,
    });
  }

  commitBoundChange(property: PropertyCode, key: string) {
    const change = this.bandChanges[property][key];
    if (!change) {
      return;
    }
    const bands = this.bandsOf(property);
    const band = bands.find((candidate) => bandKey(candidate) === key);
    change.inChange = false;
    if (!band) {
      return;
    }
    const boundary = Math.round(parseFloat(change.value) * 100) / 100;
    const edit = moveLowerBound(bands, band, boundary);
    if (edit.moved.length === 0) {
      this.$bus.$emit('notification', 'Новая граница должна лежать между соседними полосами.', 'warning');
      return;
    }
    this.applyEdit(property, edit);
  }

  cancelBoundChange(property: PropertyCode, key: string) {
    const change = this.bandChanges[property][key];
    if (change) {
      change.inChange = false;
    }
  }
}

